// v3 "Quiet Precision" primitives.
// Shared building blocks used by every v3-redesigned page so the look is
// consistent: eyebrow kicker + oversized display title + muted sub-copy,
// hairline-bordered sections, mono accents, single indigo signature.

import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

export const V3_INDIGO = "var(--v3-indigo)";
export const V3_INDIGO_FG = "var(--v3-indigo-fg)";

// ── Page shell ────────────────────────────────────────────────────────
export const V3Page = ({ children, testid }) => (
  <div
    className="min-h-screen"
    style={{ background: "var(--v3-canvas)", color: "var(--v3-text)" }}
    data-testid={testid}
  >
    {children}
  </div>
);

export const V3Container = ({ children, className = "" }) => (
  <div className={`max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14 pb-24 ${className}`}>
    {children}
  </div>
);

// ── Eyebrow kicker (mono · uppercase · indigo dot) ────────────────────
export const V3Eyebrow = ({ children, icon: Icon, dot = true, backTo, backLabel, className = "" }) => (
  <div
    className={`text-[11px] uppercase inline-flex items-center gap-2 mb-3 ${className}`}
    style={{
      color: "var(--v3-text-3)",
      letterSpacing: "0.14em",
      fontFamily: "var(--v3-font-mono)",
    }}
  >
    {dot && !Icon && !backTo && (
      <span className="h-1 w-1 rounded-full" style={{ background: V3_INDIGO }} />
    )}
    {Icon && <Icon className="w-3 h-3" strokeWidth={1.75} />}
    {backTo && (
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 hover:text-[color:var(--v3-text)] transition-colors"
      >
        <ChevronLeft className="w-3 h-3" strokeWidth={1.75} />
        {backLabel || "Back"}
      </Link>
    )}
    {backTo && children ? <span className="opacity-40">·</span> : null}
    {children}
  </div>
);

// ── Hero header: eyebrow + oversized display title + muted sub-copy ──
export const V3Header = ({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  right,
  testid,
  className = "",
}) => (
  <header className={`mb-10 ${className}`} data-testid={testid}>
    <div className="flex items-start justify-between gap-6 flex-wrap">
      <div className="min-w-0 flex-1">
        {eyebrow}
        <h1
          className="text-4xl md:text-5xl"
          style={{
            fontWeight: 500,
            letterSpacing: "-0.045em",
            lineHeight: 1.05,
          }}
        >
          {title}
          {titleAccent && (
            <>
              <br />
              <span style={{ color: "var(--v3-text-3)" }}>{titleAccent}</span>
            </>
          )}
        </h1>
        {subtitle && (
          <p
            className="mt-5 max-w-2xl text-[15px] leading-relaxed"
            style={{ color: "var(--v3-text-2)", letterSpacing: "-0.005em" }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {right && <div className="flex items-center gap-2 shrink-0">{right}</div>}
    </div>
  </header>
);

// ── Section: hairline-divided chunk with a monospace label ────────────
export const V3Section = ({ label, right, children, className = "", testid }) => (
  <section
    className={`py-8 ${className}`}
    style={{ borderTop: "1px solid var(--v3-line)" }}
    data-testid={testid}
  >
    {(label || right) && (
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        {label && <V3Label>{label}</V3Label>}
        {right}
      </div>
    )}
    {children}
  </section>
);

// Monospace section label (smaller, no indigo dot by default).
export const V3Label = ({ children, className = "" }) => (
  <span
    className={`text-[10px] uppercase ${className}`}
    style={{
      color: "var(--v3-text-3)",
      letterSpacing: "0.16em",
      fontFamily: "var(--v3-font-mono)",
      fontWeight: 500,
    }}
  >
    {children}
  </span>
);

// ── Flat hairline card (replaces nd-card / Shadcn Card chrome) ────────
export const V3Card = ({ children, className = "", testid, as: As = "div", ...rest }) => (
  <As
    className={`p-5 ${className}`}
    style={{
      background: "var(--v3-surface)",
      border: "1px solid var(--v3-line)",
      ...(rest.style || {}),
    }}
    data-testid={testid}
    {...rest}
  >
    {children}
  </As>
);

// ── Underline pill tabs (same as SchedulePage day-strip pattern) ─────
export const V3Tabs = ({ tabs, value, onChange, testidPrefix = "v3-tab" }) => (
  <div
    className="flex items-center gap-1 overflow-x-auto -mx-1 px-1"
    style={{ borderBottom: "1px solid var(--v3-line)" }}
  >
    {tabs.map((t) => {
      const active = value === t.value;
      return (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          data-testid={t.testid || `${testidPrefix}-${t.value}`}
          className="relative px-3 py-3 text-[13px] transition-colors inline-flex items-center gap-1.5 shrink-0"
          style={{
            color: active ? "var(--v3-text)" : "var(--v3-text-3)",
            fontWeight: active ? 500 : 400,
            letterSpacing: "-0.005em",
          }}
        >
          {t.icon ? <t.icon className="w-[13px] h-[13px]" strokeWidth={1.75} /> : null}
          {t.label}
          {t.count != null && (
            <span
              className="ml-1 text-[11px]"
              style={{
                color: active ? "var(--v3-text-3)" : "var(--v3-text-4)",
                fontFamily: "var(--v3-font-mono)",
              }}
            >
              {t.count}
            </span>
          )}
          {active && (
            <span
              className="absolute left-0 right-0 -bottom-px h-px"
              style={{ background: "var(--v3-text)" }}
            />
          )}
        </button>
      );
    })}
  </div>
);

// ── Mono tag pill (for small badges and tier markers) ────────────────
export const V3Tag = ({ children, tone = "mute", className = "", testid }) => {
  const palettes = {
    mute: { color: "var(--v3-text-3)", border: "var(--v3-line-2)", bg: "transparent" },
    indigo: {
      color: "var(--v3-indigo-fg)",
      border: "rgba(99,102,241,0.35)",
      bg: "rgba(99,102,241,0.08)",
    },
    emerald: {
      color: "#86efac",
      border: "rgba(34,197,94,0.35)",
      bg: "rgba(34,197,94,0.08)",
    },
    rose: {
      color: "#fda4af",
      border: "rgba(244,63,94,0.35)",
      bg: "rgba(244,63,94,0.08)",
    },
    amber: {
      color: "#fcd34d",
      border: "rgba(234,179,8,0.35)",
      bg: "rgba(234,179,8,0.08)",
    },
  };
  const p = palettes[tone] || palettes.mute;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase ${className}`}
      style={{
        color: p.color,
        border: `1px solid ${p.border}`,
        background: p.bg,
        letterSpacing: "0.12em",
        fontFamily: "var(--v3-font-mono)",
        fontWeight: 500,
      }}
      data-testid={testid}
    >
      {children}
    </span>
  );
};

// ── Flat segmented control (replaces rounded-full pill groups) ────────
export const V3Segment = ({ options, value, onChange, size = "sm", testidPrefix }) => (
  <div
    className="inline-flex items-center"
    style={{ border: "1px solid var(--v3-line)", padding: 2 }}
  >
    {options.map((o) => {
      const active = value === o.value;
      const padX = size === "sm" ? 12 : 14;
      const padY = size === "sm" ? 4 : 6;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          data-testid={o.testid || (testidPrefix ? `${testidPrefix}-${o.value}` : undefined)}
          className="text-[11px] uppercase transition-colors"
          style={{
            padding: `${padY}px ${padX}px`,
            background: active ? "var(--v3-text)" : "transparent",
            color: active ? "var(--v3-canvas)" : "var(--v3-text-3)",
            fontFamily: "var(--v3-font-mono)",
            letterSpacing: "0.12em",
            fontWeight: 500,
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

// ── Primary indigo button (compact, flat, no rounded-full) ────────────
export const V3Button = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  ...rest
}) => {
  const base = "inline-flex items-center justify-center gap-1.5 transition-colors";
  const sizes = { sm: "h-7 px-3 text-[11px]", md: "h-9 px-4 text-[12px]", lg: "h-10 px-5 text-[13px]" };
  const style =
    variant === "primary"
      ? { background: V3_INDIGO, color: "var(--v3-text)" }
      : variant === "ghost"
      ? { background: "transparent", color: "var(--v3-text-2)", border: "1px solid var(--v3-line-2)" }
      : { background: "var(--v3-raised)", color: "var(--v3-text)", border: "1px solid var(--v3-line-2)" };
  return (
    <button
      className={`${base} ${sizes[size]} ${className}`}
      style={{
        fontFamily: "var(--v3-font-mono)",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontWeight: 500,
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
};

// Monospace number cell: tnum numerics for aligned stats.
export const V3Num = ({ children, tone, className = "" }) => (
  <span
    className={`font-mono ${className}`}
    style={{
      color: tone || "var(--v3-text)",
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.01em",
    }}
  >
    {children}
  </span>
);

// Stat tile — flat, Linear-style metric slab.
export const V3Stat = ({ label, value, sub, tone, testid, className = "" }) => (
  <div
    className={`p-5 ${className}`}
    style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
    data-testid={testid}
  >
    <V3Label>{label}</V3Label>
    <div
      className="mt-3 text-3xl md:text-4xl"
      style={{
        color: tone || "var(--v3-text)",
        fontFamily: "var(--v3-font-mono)",
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.03em",
        fontWeight: 500,
      }}
    >
      {value}
      {sub && (
        <span
          className="ml-1 text-base"
          style={{ color: "var(--v3-text-3)" }}
        >
          {sub}
        </span>
      )}
    </div>
  </div>
);
